import { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from "@mui/material";

import { useAuthedApi } from "@/api/useAuthedApi";

/** Step 4: Connect recommended tools. Nothing is auto-enabled —
 *  tiles show connected/not-connected state. Skip and Done both
 *  mark tools complete and navigate to /workspace. */
export function ToolsStep() {
  const { onboarding, connectors } = useAuthedApi();
  const [recommended, setRecommended] = useState<string[]>([]);
  const [connected, setConnected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      onboarding.getContext(),
      connectors.list().catch(() => []),
    ])
      .then(([ctx, connList]) => {
        setRecommended(ctx.recommended_connectors ?? []);
        // Mark already-connected connectors
        const connectedIds = new Set(
          (connList as { id: string; connected: boolean }[])
            .filter((c) => c.connected)
            .map((c) => c.id),
        );
        setConnected(connectedIds);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [onboarding, connectors]);

  async function markComplete() {
    setBusy(true);
    try {
      await onboarding.postContext({ tools_completed: true });
    } catch {
      // Non-fatal
    }
    window.location.assign("/workspace");
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Connect your tools
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Based on your profile, we recommend connecting these tools.
        You can always add more later from the Plugins page.
      </Typography>

      {recommended.length === 0 && (
        <Alert severity="info">
          No specific recommendations — you can connect tools anytime from the Plugins page.
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 1.5 }}>
        {recommended.map((connectorId) => {
          const isConnected = connected.has(connectorId);
          return (
            <Card
              key={connectorId}
              variant="outlined"
              sx={{
                textAlign: "center",
                opacity: isConnected ? 0.7 : 1,
                position: "relative",
              }}
            >
              <CardContent sx={{ py: 2, px: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                  {connectorId.replace(/_/g, " ")}
                </Typography>
                {isConnected && (
                  <Typography
                    color="success.main"
                    sx={{ position: "absolute", top: 4, right: 4, fontSize: 14, fontWeight: 700 }}
                  >
                    ✓
                  </Typography>
                )}
                {!isConnected && (
                  <Button
                    size="small"
                    variant="text"
                    href={`/workspace/plugins`}
                    sx={{ mt: 0.5, fontSize: "0.7rem" }}
                  >
                    Connect
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
        <Button variant="text" onClick={markComplete} disabled={busy}>
          Skip
        </Button>
        <Button variant="contained" onClick={markComplete} disabled={busy}>
          Done
        </Button>
      </Stack>
    </Stack>
  );
}
